import React from 'react'
import { MyselfFragment } from '#src/generated/graphql'
import { IdentityConsumer } from '../Identity'
import { withAnalytics, WithAnalyticsProps as WithAnalyticsPropsBase } from './withAnalytics'
import { getUserAcquisitionAttributes } from '#src/services/tracking'
import { Analytics } from '#src/tracking'

// Context
type AnalyticsContext = Analytics | undefined

export const AnalyticsContext = React.createContext<AnalyticsContext>(undefined)

// Consumer
const AnalyticsConsumer = AnalyticsContext.Consumer

interface BrowserAnalyticsProviderProps {
  children: (analytics?: Analytics) => React.ReactNode
  myself: MyselfFragment | null
  analytics: Analytics
}

class BrowserAnalyticsProvider extends React.Component<BrowserAnalyticsProviderProps> {
  constructor(props: BrowserAnalyticsProviderProps) {
    super(props)

    this.identifyUser = this.identifyUser.bind(this)
    this.setUserAcquisitionTraits = this.setUserAcquisitionTraits.bind(this)

    if (process.env.TARGET === 'web') {
      const currentUser = this.userId(this.props)
      this.setUserAcquisitionTraits(this.props.myself)

      if (this.props.myself && currentUser) {
        this.identifyUser(this.props.myself, currentUser)
      }
    }
  }

  public componentDidUpdate(prevProps: BrowserAnalyticsProviderProps) {
    const currentUser = this.userId(this.props)
    const previousUser = this.userId(prevProps)

    if (currentUser !== previousUser && currentUser && this.props.myself) {
      // logging in, or changing users
      this.identifyUser(this.props.myself, currentUser)
    } else if (previousUser && !currentUser) {
      // Logout
      this.props.analytics && this.props.analytics.reset()
    }
  }

  public isImpersonating(myself: MyselfFragment | null) {
    if (!myself) {
      return false
    }

    return myself.realUser.id !== myself.user.id
  }

  private userId(props: BrowserAnalyticsProviderProps) {
    const { myself } = props
    return myself && myself.user && myself.user.id
  }

  private identifyUser(myself: MyselfFragment, userId: string) {
    if (this.isImpersonating(myself)) {
      return
    }

    const { firstName, lastName, contacts, address } = myself.user

    const email = contacts.find(contact => contact.type.toUpperCase() === 'EMAIL')
    const phone = contacts.find(contact => contact.type.toUpperCase() === 'PHONE')
    const facebook = contacts.find(contact => contact.type.toUpperCase() === 'FACEBOOK')

    const userAddress = address
      ? {
          street: address.line1 || undefined,
          city: address.city || undefined,
          state: address.state || undefined,
          postalCode: address.postalCode || undefined
        }
      : undefined

    const traits = {
      firstName: firstName ? firstName : undefined,
      lastName: lastName ? lastName : undefined,
      email: email ? email.value : undefined,
      phone: phone ? phone.value : undefined,
      facebook: facebook ? facebook.value : undefined,
      address: userAddress
    }

    if (this.props.analytics) {
      this.props.analytics.addUserTraits(traits)
      this.props.analytics.identify(userId)
    }
  }

  public setUserAcquisitionTraits(myself: MyselfFragment | null) {
    if (this.isImpersonating(myself)) {
      return
    }

    const userAcquisitionAttributes = getUserAcquisitionAttributes()

    this.props.analytics.addUserTraits(userAcquisitionAttributes)
  }

  public render() {
    const isImpersonating = this.isImpersonating(this.props.myself)

    return this.props.children(isImpersonating ? undefined : this.props.analytics)
  }
}

interface AnalyticsProviderProps {
  analytics?: Analytics
  children: React.ReactElement<any>
}

const AnalyticsProvider: React.SFC<AnalyticsProviderProps> = ({ children, analytics }) => {
  if (analytics) {
    return (
      <IdentityConsumer>
        {({ myself }) => (
          <BrowserAnalyticsProvider myself={myself} analytics={analytics}>
            {analytics => {
              return (
                <AnalyticsContext.Provider value={analytics}>{children}</AnalyticsContext.Provider>
              )
            }}
          </BrowserAnalyticsProvider>
        )}
      </IdentityConsumer>
    )
  } else {
    return <AnalyticsContext.Provider value={undefined}>{children}</AnalyticsContext.Provider>
  }
}

export interface WithAnalyticsProps extends WithAnalyticsPropsBase {}
export { AnalyticsProvider, AnalyticsConsumer, withAnalytics }
